// src/utils/firebase/sales.js
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  Timestamp,
  writeBatch,
  runTransaction,
  increment
} from 'firebase/firestore';
import { db } from './firebase.utils';
import { SALE_KIND, SALE_STATUSES, canMarkSold, isSold, isStaged, normalizeSaleStatus } from '../sales/sales.constants';
import { calculateNetProfit } from '../sales/salesMath';
import { releaseSaleCardsFromCollection, restoreSaleCardsToCollection, getSaleCards } from './releaseCards';
import { syncGroupCodePreviewForSale } from './groupCodes';
import { mintEbaySkus } from './ebay';

// Re-exported so existing callers can keep importing it from here
export { calculateNetProfit };

/**
 * Sanitizes sale data to ensure proper Firestore format
 * @param {Object} saleData - Sale data to sanitize
 * @returns {Object} Sanitized sale data
 */
const sanitizeSaleData = (saleData) => {
  const sanitized = {
    ...saleData,
    status: saleData.status || SALE_STATUSES.LISTED,
    listingDate: saleData.listingDate || Timestamp.now(),
    saleDate: saleData.saleDate || null,
    kind: saleData.kind || SALE_KIND,
    platform: saleData.platform || '',
    price: saleData.price || saleData.salePrice || saleData.listingPrice || 0, // Support backward compatibility
    salesTax: saleData.salesTax || 0,
    buyerShipping: saleData.buyerShipping || 0,
    shippingCost: saleData.shippingCost || 0,
    transactionFee: saleData.transactionFee || 0,
    platformFee: saleData.platformFee || 0,
    cardCount: saleData.cardCount || 0,
    packingCost: saleData.packingCost || 0,
    notes: saleData.notes || '',
    createdAt: saleData.createdAt || Timestamp.now(),
    updatedAt: Timestamp.now()
  };

  // Calculate net profit
  sanitized.netProfit = calculateNetProfit(sanitized);

  return sanitized;
};

const listingCodeSource = (platform) => {
  const normalized = String(platform ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalized || 'sale';
};

/**
 * Creates a new sale document
 * @param {string} userID - User ID
 * @param {Object} saleData - Sale data
 * @returns {Promise<{saleId: string, saleData: Object}>}
 */
export const createSale = async (userID, saleData) => {
  try {
    const salesCollectionRef = collection(db, `users/${userID}/transactions`);
    const saleDocRef = doc(salesCollectionRef);
    const saleId = saleDocRef.id;

    let ebaySku = saleData.ebaySku ?? null;
    if (!ebaySku) {
      const [sku] = await mintEbaySkus(userID, 1, listingCodeSource(saleData.platform));
      ebaySku = sku;
    }

    const sanitizedData = sanitizeSaleData({
      ...saleData,
      ebaySku,
      createdAt: Timestamp.now()
    });

    await setDoc(saleDocRef, sanitizedData);
    await syncGroupCodePreviewForSale(userID, saleId, { sale: sanitizedData, cards: [] });

    return {
      saleId,
      saleData: {
        id: saleId,
        ...sanitizedData
      }
    };
  } catch (error) {
    // Error creating sale
    throw error;
  }
};

/**
 * Updates an existing sale document
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>}
 */
export const updateSale = async (userID, saleId, updateData) => {
  try {
    const saleDocRef = doc(db, `users/${userID}/transactions/${saleId}`);
    
    const sanitizedUpdate = {
      ...updateData,
      updatedAt: Timestamp.now()
    };

    const needsProfit = updateData.price !== undefined || 
        updateData.salePrice !== undefined || 
        updateData.listingPrice !== undefined ||
        updateData.buyerShipping !== undefined ||
        updateData.salesTax !== undefined ||
        updateData.shippingCost !== undefined || 
        updateData.transactionFee !== undefined || 
        updateData.platformFee !== undefined || 
        updateData.packingCost !== undefined;
    const maybePreview = updateData.title !== undefined || updateData.status !== undefined;

    let currentData = {};
    if (needsProfit || maybePreview) {
      const currentSale = await getDoc(saleDocRef);
      currentData = currentSale.exists() ? currentSale.data() : {};
    }

    if (needsProfit) {
      sanitizedUpdate.netProfit = calculateNetProfit({ ...currentData, ...sanitizedUpdate });
    }

    await updateDoc(saleDocRef, sanitizedUpdate);

    const titleChanged = updateData.title !== undefined &&
      String(updateData.title || '').trim() !== String(currentData.title || '').trim();
    const statusChanged = updateData.status !== undefined &&
      normalizeSaleStatus(updateData.status) !== normalizeSaleStatus(currentData.status);

    if (titleChanged || statusChanged) {
      await syncGroupCodePreviewForSale(userID, saleId, {
        sale: { ...currentData, ...sanitizedUpdate }
      });
    }

    return {
      saleId,
      ...sanitizedUpdate
    };
  } catch (error) {
    // Error updating sale
    throw error;
  }
};

/**
 * Marks a sale as sold and releases its cards from the collection.
 *
 * Sold is deliberately one-way: reverting the status or deleting the sale never
 * re-adds the cards, so the release only ever has to run forward. If the release
 * fails partway, calling this again resumes it — per-card flags prevent any card
 * from being decremented twice.
 *
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @param {Object} saleFields - Final sale values to save alongside the status
 * @returns {Promise<{saleId: string, release: Object}>}
 */
export const markSaleAsSold = async (userID, saleId, saleFields = {}) => {
  const saleDocRef = doc(db, `users/${userID}/transactions/${saleId}`);
  const saleSnap = await getDoc(saleDocRef);

  if (!saleSnap.exists()) throw new Error('Sale not found');

  const saleData = saleSnap.data();
  const alreadyReleased = !!saleData.collectionReleasedAt;

  if (!canMarkSold(saleData.status) && alreadyReleased) {
    throw new Error('This sale is already marked as sold');
  }

  await updateSale(userID, saleId, {
    ...saleFields,
    status: SALE_STATUSES.SOLD,
    saleDate: saleFields.saleDate || Timestamp.now()
  });

  const cards = await getSaleCards(userID, saleId);
  const release = await releaseSaleCardsFromCollection(userID, saleId, cards);

  return { saleId, release };
};

/**
 * Reopens a sold sale, putting the cards it removed back into the collection.
 *
 * This is the correction path for a sale marked sold by mistake. The sale's
 * figures are left as entered; only the status and the collection are reversed.
 *
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @returns {Promise<{saleId: string, restore: Object}>}
 */
export const revertSaleToListed = async (userID, saleId) => {
  const saleDocRef = doc(db, `users/${userID}/transactions/${saleId}`);
  const saleSnap = await getDoc(saleDocRef);

  if (!saleSnap.exists()) throw new Error('Sale not found');
  if (!isSold(saleSnap.data().status)) throw new Error('This sale is not marked as sold');

  const cards = await getSaleCards(userID, saleId);
  const restore = await restoreSaleCardsToCollection(userID, saleId, cards);

  await updateDoc(saleDocRef, {
    status: SALE_STATUSES.LISTED,
    updatedAt: Timestamp.now()
  });
  await syncGroupCodePreviewForSale(userID, saleId);

  return { saleId, restore };
};

/**
 * Gets a single sale document with its cards subcollection
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @returns {Promise<Object>}
 */
// Helper function to serialize Firestore Timestamps
const serializeTimestamp = (value) => {
  if (!value) return value;
  // Check if it's a Firestore Timestamp with seconds/nanoseconds
  if (value && typeof value === 'object' && value.seconds !== undefined && value.nanoseconds !== undefined) {
    return new Date(value.seconds * 1000 + value.nanoseconds / 1000000).toISOString();
  }
  // Handle Firestore Timestamp with toDate method
  if (value && typeof value === 'object' && typeof value.toDate === 'function') {
    return value.toDate().toISOString();
  }
  return value;
};

// Helper function to serialize an object recursively
const serializeObject = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => serializeObject(item));
  }
  const serialized = {};
  Object.keys(obj).forEach(key => {
    serialized[key] = serializeObject(serializeTimestamp(obj[key]));
  });
  return serialized;
};

export const getSale = async (userID, saleId) => {
  try {
    const saleDocRef = doc(db, `users/${userID}/transactions/${saleId}`);
    const saleSnap = await getDoc(saleDocRef);

    if (!saleSnap.exists()) {
      return null;
    }

    // Serialize sale data
    const saleData = serializeObject(saleSnap.data());

    // Get cards subcollection
    const cardsCollectionRef = collection(db, `users/${userID}/transactions/${saleId}/cards`);
    const cardsSnap = await getDocs(cardsCollectionRef);
    const cards = cardsSnap.docs.map(doc => {
      const cardData = doc.data();
      return serializeObject({ id: doc.id, ...cardData });
    });

    // Get supplies subcollection
    const suppliesCollectionRef = collection(db, `users/${userID}/transactions/${saleId}/supplies`);
    const suppliesSnap = await getDocs(suppliesCollectionRef);
    const supplies = suppliesSnap.docs.map(doc => {
      const supplyData = doc.data();
      return serializeObject({ id: doc.id, ...supplyData });
    });

    // Sales predating the denormalized count, or drifted by an interrupted
    // write, are corrected here — this is the one read that knows the truth.
    if (saleData.cardCount !== cards.length) {
      await updateDoc(saleDocRef, { cardCount: cards.length });
    }

    return {
      ...saleData,
      id: saleId,
      cardCount: cards.length,
      cards,
      supplies
    };
  } catch (error) {
    // Error getting sale
    throw error;
  }
};

/**
 * Gets all sales for a user
 * @param {string} userID - User ID
 * @param {Object} filters - Optional filters (status, platform, etc.)
 * @returns {Promise<Array>}
 */
export const getSales = async (userID, filters = {}) => {
  try {
    const salesCollectionRef = collection(db, `users/${userID}/transactions`);
    const salesSnap = await getDocs(salesCollectionRef);
    
    let sales = salesSnap.docs.map(doc => {
      const data = doc.data();
      return serializeObject({ id: doc.id, ...data });
    }).filter((sale) => !isStaged(sale.status));

    // Apply filters
    if (filters.status) {
      sales = sales.filter(sale => sale.status === filters.status);
    }
    if (filters.platform) {
      sales = sales.filter(sale => sale.platform === filters.platform);
    }

    // Sort by listingDate descending (newest first)
    sales.sort((a, b) => {
      const dateA = a.listingDate ? new Date(a.listingDate).getTime() : 0;
      const dateB = b.listingDate ? new Date(b.listingDate).getTime() : 0;
      return dateB - dateA;
    });

    return sales;
  } catch (error) {
    // Error getting sales
    throw error;
  }
};

/**
 * Deletes a sale document and its cards subcollection
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @returns {Promise<void>}
 */
export const deleteSale = async (userID, saleId) => {
  try {
    // Delete all cards in subcollection first
    const cardsCollectionRef = collection(db, `users/${userID}/transactions/${saleId}/cards`);
    const cardsSnap = await getDocs(cardsCollectionRef);
    const batch = writeBatch(db);
    
    cardsSnap.docs.forEach(cardDoc => {
      batch.delete(cardDoc.ref);
    });

    // Delete the sale document
    const saleDocRef = doc(db, `users/${userID}/transactions/${saleId}`);
    batch.delete(saleDocRef);

    await batch.commit();
  } catch (error) {
    // Error deleting sale
    throw error;
  }
};

/**
 * Adds a card to a sale's cards subcollection
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @param {Object} cardData - Card data
 * @returns {Promise<{cardId: string, cardData: Object}>}
 */
export const addCardToSale = async (userID, saleId, cardData) => {
  try {
    const cardsCollectionRef = collection(db, `users/${userID}/transactions/${saleId}/cards`);
    const cardDocRef = doc(cardsCollectionRef);
    const cardId = cardDocRef.id;

    const sanitizedCardData = {
      ...cardData,
      quantity: cardData.quantity || 1,
      createdAt: Timestamp.now()
    };

    await setDoc(cardDocRef, sanitizedCardData);
    // The Tracker and Dashboard read this denormalized count rather than the
    // subcollection, so it has to move with the cards.
    await updateDoc(doc(db, `users/${userID}/transactions/${saleId}`), {
      cardCount: increment(1),
      updatedAt: Timestamp.now()
    });
    await syncGroupCodePreviewForSale(userID, saleId);

    return {
      cardId,
      cardData: {
        id: cardId,
        ...sanitizedCardData
      }
    };
  } catch (error) {
    // Error adding card to sale
    throw error;
  }
};

/**
 * Removes a card from a sale's cards subcollection
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @param {string} cardId - Card document ID
 * @returns {Promise<void>}
 */
export const removeCardFromSale = async (userID, saleId, cardId) => {
  try {
    const cardDocRef = doc(db, `users/${userID}/transactions/${saleId}/cards/${cardId}`);
    await deleteDoc(cardDocRef);
    await updateDoc(doc(db, `users/${userID}/transactions/${saleId}`), {
      cardCount: increment(-1),
      updatedAt: Timestamp.now()
    });
    await syncGroupCodePreviewForSale(userID, saleId);
  } catch (error) {
    // Error removing card from sale
    throw error;
  }
};

/**
 * Updates a card in a sale's cards subcollection
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @param {string} cardId - Card document ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>}
 */
export const updateSaleCard = async (userID, saleId, cardId, updateData) => {
  try {
    const cardDocRef = doc(db, `users/${userID}/transactions/${saleId}/cards/${cardId}`);
    await updateDoc(cardDocRef, {
      ...updateData,
      updatedAt: Timestamp.now()
    });
    await syncGroupCodePreviewForSale(userID, saleId);

    return {
      cardId,
      ...updateData
    };
  } catch (error) {
    // Error updating sale card
    throw error;
  }
};

/**
 * Adds an inventory supply usage to a sale and decrements inventory quantity
 * @param {string} userID - User ID
 * @param {string} saleId - Sale document ID
 * @param {Object} supplyData - { inventoryItemId: string, qtyUsed: number }
 * @returns {Promise<{supplyId: string, supplyData: Object}>}
 */
export const addSupplyToSale = async (userID, saleId, supplyData) => {
  try {
    const inventoryDocRef = doc(db, `users/${userID}/inventory/${supplyData.inventoryItemId}`);
    const saleDocRef = doc(db, `users/${userID}/transactions/${saleId}`);
    const suppliesCollectionRef = collection(db, `users/${userID}/transactions/${saleId}/supplies`);
    const supplyDocRef = doc(suppliesCollectionRef);
    const supplyId = supplyDocRef.id;

    const result = await runTransaction(db, async (transaction) => {
      const inventorySnap = await transaction.get(inventoryDocRef);
      if (!inventorySnap.exists()) {
        throw new Error('Inventory item not found');
      }

      const inventory = inventorySnap.data();
      const inventoryQty = parseInt(inventory.quantity, 10) || 0;
      const qtyUsed = parseInt(supplyData.qtyUsed, 10) || 0;

      if (qtyUsed === 0) {
        throw new Error('Quantity must be a non-zero number');
      }
      if (qtyUsed > 0 && inventoryQty < qtyUsed) {
        throw new Error('Not enough inventory available');
      }

      const totalCost = parseFloat(inventory.totalCost) || 0;
      const unitCost = inventoryQty > 0 ? totalCost / inventoryQty : 0;
      const totalCostUsed = Math.round(unitCost * qtyUsed * 100) / 100;

      const saleSnap = await transaction.get(saleDocRef);
      if (!saleSnap.exists()) {
        throw new Error('Sale not found');
      }
      const saleData = saleSnap.data();
      const currentPackingCost = saleData.packingCost || 0;
      const newPackingCost = Math.round((currentPackingCost + totalCostUsed) * 100) / 100;
      const updatedSale = {
        packingCost: newPackingCost,
        updatedAt: Timestamp.now(),
        netProfit: calculateNetProfit({ ...saleData, packingCost: newPackingCost })
      };

      transaction.update(inventoryDocRef, {
        quantity: inventoryQty - qtyUsed,
        updatedAt: Timestamp.now()
      });
      transaction.update(saleDocRef, updatedSale);
      transaction.set(supplyDocRef, {
        inventoryItemId: supplyData.inventoryItemId,
        itemName: inventory.itemName || '',
        qtyUsed,
        unitCost,
        totalCost: totalCostUsed,
        createdAt: Timestamp.now()
      });

      return {
        supplyId,
        supplyData: {
          id: supplyId,
          inventoryItemId: supplyData.inventoryItemId,
          itemName: inventory.itemName || '',
          qtyUsed,
          unitCost,
          totalCost: totalCostUsed
        }
      };
    });

    return result;
  } catch (error) {
    // Error adding supply to sale
    throw error;
  }
};

